import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/use-permissions";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { client, MinimalEntityDto } from "@/client";
import { DialogClose, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/custom/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useNavigate } from "@tanstack/react-router";
import { useTable } from "@/components/custom/data-table/data-table-context";
import { Input } from "@/components/ui/input";
import DialogComponent from "@/components/custom/dialog";
import SelectInput, {
  OptionType,
} from "@/components/select-input/select-input";
import { GroupBase } from "react-select";

import { showActionErrorToast } from "@/state/error-toast";

export const AddSiteGroup = () => {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  // Gated like the route: POST /site-groups is siteGroups.create (Tech, Owner, EdgifyCandidate, MLOps).
  if (permissions.siteGroups.create) {
    return (
      <DialogComponent
        label={t("addSiteGroup")}
        title={t("addNewSiteGroup")}
        contentClassName={"max-w-[50vh]"}
      >
        <AddSiteGroupForm />
      </DialogComponent>
    );
  }
  return <></>;
};

const formSchema = z.object({
  siteGroupName: z.string().min(1, { message: "Please enter site group name" }),
  siteIds: z.array(z.object({ value: z.number(), label: z.string() })),
});

const AddSiteGroupForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: allSitesMinimalResults } = useQuery({
    ...client.sites.listMinimal
      .assign({
        page: 1,
        limit: 9999,
      })
      .asQueryOptions({}),
  });
  const { data: unassignedSitesResults } = useQuery({
    ...client.sites.listMinimal
      .assign({
        page: 1,
        limit: 9999,
        groupId: -1,
      })
      .asQueryOptions({}),
  });

  const { groupedOptions } = useMemo(() => {
    //
    const allSitesMap = new Map(
      (allSitesMinimalResults?.results ?? []).map((site: MinimalEntityDto) => [
        site.id,
        site,
      ]),
    );
    (unassignedSitesResults?.results ?? []).forEach((site: MinimalEntityDto) =>
      allSitesMap.delete(site.id),
    );
    const sitesRest: OptionType[] = [...allSitesMap.values()].map((value) => ({
      label: value.name,
      value: value.id,
    }));
    //
    const unassignedSites: OptionType[] = (
      unassignedSitesResults?.results ?? []
    )
      .map((result) => {
        if (
          result !== null &&
          typeof result === "object" &&
          "id" in result &&
          "name" in result
        ) {
          return {
            label: result.name as any as string,
            value: result.id as any as number,
          };
        } else {
          return null;
        }
      })
      .filter((element) => {
        return element !== null;
      })
      .sort((a, b) => a.value - b.value);
    //
    const groupedOptions: readonly GroupBase<{
      value: number;
      label: string;
    }>[] = [
      {
        label: t("unassignedSites"),
        options: unassignedSites as readonly {
          value: number;
          label: string;
        }[],
      },
      {
        label: t("assignedSites"),
        options: sitesRest as readonly { value: number; label: string }[],
      },
    ];

    return {
      groupedOptions,
    };
  }, [allSitesMinimalResults, unassignedSitesResults, t]);

  const createSiteGroup = useMutation({
    ...client.siteGroups.create.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
  });
  const table = useTable();
  const queryClient = useQueryClient();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      siteGroupName: "",
      siteIds: [],
    },
  });
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const siteIds = data.siteIds.map((site) => site.value);
    const siteGroup = (await createSiteGroup.mutateAsync({
      requestBody: { name: data.siteGroupName, siteIds },
    })) as any;
    table?.setPreview(2);
    table?.setPosition({});
    queryClient.invalidateQueries({
      queryKey: client.siteGroups.listSummaries.queryKey,
    });
    navigate({
      to: "/dashboard/resources/site-groups/$siteGroupId",
      params: { siteGroupId: siteGroup.id as any },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="pb-4 flex flex-col gap-4">
          <FormField
            control={form.control}
            name="siteGroupName"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel>{t("siteGroupName")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("addSiteGroup")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="siteIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("associateSites")}</FormLabel>
                <FormControl>
                  <SelectInput
                    placeholder={t("selectSites")}
                    {...field}
                    classNames={{
                      control: () => "min-h-16 max-h-28 overflow-y-scroll",
                    }}
                    isMulti={true}
                    isClearable={true}
                    options={groupedOptions}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button">{t("cancel")}</Button>
          </DialogClose>
          <Button
            variant="azure"
            disabled={!form.formState.isDirty || !form.formState.isValid}
          >
            {t("done")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
